import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { nanoid } from "nanoid";
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Icon24Lock, Icon24Unlock } from "../Icons";
import { IMarker } from "../RichEditor/markers.plugin";
import RichEditor, {
  IRichEditorRef,
  ISelection,
  RichEditorMode,
} from "../RichEditor/RichEditor";
import { useAutoScrollToFieldWithError } from "../../hooks/useAutoScrollToFieldWithError";

export { RichEditorMode };

export enum TextAreaMinRows {
  OneLine = 1,
  MultiLine = 4,
}

interface TextAreaCustomProps {
  name: string;
  value?: string;
  label?: React.ReactNode;
  active?: boolean;
  description?: string;
  error?: string;
  freeze?: boolean;
  disabled?: boolean;
  minRows?: TextAreaMinRows;
  placeholder?: string;
  className?: string;
  mode?: RichEditorMode;
  stopWordsList?: { word: string; caption: string }[];
  onChange?: (value: string) => void;
  after?: (focused: boolean) => ReactNode;
  markers?: IMarker[];
  onFocus?: () => void;
  onBlur?: () => void;
  onClick?: () => void;
  onDisabledToggle?: (value: boolean) => void;
  onTextSelectionUpdate?: (selection: ISelection) => void;
  onMarkerSelected?: (markerId: string) => void;
  children?: ReactNode;
  enableVoiceRecorder?: boolean;
  onVoiceTranscriptionComplete?: (text: string) => void;
  voiceRecorderComponent?: ReactNode;
  isTranscribing?: boolean;
}

export type TextAreaProps = TextAreaCustomProps;

export const useForwardedRef = <T extends IRichEditorRef = IRichEditorRef>(
  ref: ForwardedRef<T>
) => {
  const innerRef = useRef<T>(null);

  useEffect(() => {
    if (!ref) return;
    if (typeof ref === "function") {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  });

  return innerRef;
};

const TextAreaWithRaisingLabel = forwardRef<IRichEditorRef, TextAreaProps>(
  (props, ref) => {
    const [value, setValue] = useState(props.value ?? "");
    const [error, setError] = useState(props.error ?? "");
    const [focus, setFocus] = useState(props.active ?? false);
    const [labelMaxWidth, setLabelMaxWidth] = useState("0px");
    const [id, setId] = useState(props.name ?? "");
    const fieldElement = useForwardedRef(ref);
    const elementRef = useRef<HTMLDivElement>(null);
    const inactive = props.disabled || props.freeze;

    useAutoScrollToFieldWithError(props.name, elementRef.current, !!error);

    const { name, active, onChange, onClick, onDisabledToggle, disabled } =
      props;
    useEffect(() => {
      setId(`${name}${nanoid()}`);
    }, [name]);

    useEffect(() => {
      setValue(props.value ?? "");
    }, [setValue, props.value]);

    useEffect(() => {
      setError(props.error ?? "");
    }, [props.error, setError]);

    useEffect(() => {
      if (active) {
        inactive && fieldElement.current?.focus();
      }
      setFocus(active ?? false);
    }, [fieldElement, active, inactive, setFocus]);

    useEffect(() => {
      if (fieldElement && fieldElement.current) {
        setLabelMaxWidth(
          getComputedStyle(fieldElement.current.getContentElement()).width
        );
      }
    }, [fieldElement, props.after]);

    useEffect(() => {
      function onMarkerCreated(event: CustomEvent) {
        const { detail } = event;
        const segments = detail.name.split("_");
        const detailName = segments.length
          ? segments[segments.length - 1]
          : detail.name;

        if (detailName !== props.name) {
          return;
        }

        if (fieldElement.current) {
          fieldElement.current.createMarker(
            {
              from: detail.selection.from,
              to: detail.selection.to,
            },
            {
              id: detail.id,
              color: detail.color,
            }
          );
        }
      }

      function onMarkerDeleted(event: CustomEvent) {
        const { detail } = event;

        if (detail.name !== props.name) {
          return;
        }

        if (fieldElement.current) {
          fieldElement.current.removeMarker(detail.id);
        }
      }

      document.addEventListener("text:marker:created", onMarkerCreated as any);
      document.addEventListener("text:marker:deleted", onMarkerDeleted as any);

      return () => {
        document.removeEventListener(
          "text:marker:created",
          onMarkerCreated as any
        );
        document.removeEventListener(
          "text:marker:deleted",
          onMarkerDeleted as any
        );
      };
    });

    const handleChange = useCallback(
      (value: string) => {
        if (onChange) {
          onChange(value);
        }
        setValue(value);
      },
      [onChange, setValue]
    );

    const handleFocus = useCallback(() => {
      setFocus(true);
      props.onFocus && props.onFocus();
    }, [setFocus, props]);

    const handleBlur = useCallback(() => {
      setFocus(false);
      setError(props.error ?? "");
      props.onBlur && props.onBlur();
    }, [setFocus, props]);

    const handleClick = useCallback(() => {
      onClick?.();
    }, [onClick]);

    const handleFreezeToggle = useCallback(() => {
      onDisabledToggle?.(!disabled);
    }, [onDisabledToggle, disabled]);

    // eslint-disable-next-line unused-imports/no-unused-vars
    const { description, freeze, ...restProps } = props;

    return (
      <div className="min-w-64 inline-flex w-full flex-col" ref={elementRef}>
        <div className="relative w-full">
          <div
            onClick={handleClick}
            onFocus={() => handleFocus()}
            onBlur={() => handleBlur()}
            className="group relative inline-flex w-full"
          >
            <RichEditor
              stopWordsList={props.stopWordsList}
              markers={props.markers}
              ref={fieldElement}
              value={props.value || ""}
              wrapperClassName={clsxm(
                "relative w-full resize-none rounded-2xl",
                "border border-solid border-gray-150 bg-gray-150 placeholder-black-400 transition",
                "hover:bg-gray-150 focus:border-gray-150 focus:outline-none focus:ring-0",
                error &&
                  "border-2 border-blood-600 bg-gray-100 focus:border-blood-600",
                !focus && !error && !props.disabled && "border-transparent",
                focus || props.disabled
                  ? "bg-white-900 hover:bg-white-900"
                  : "bg-gray-100",
                props.disabled && "cursor-default pr-12 text-black-400",
                props.freeze && "cursor-default pr-12 text-black-900",
                !focus && !inactive && "hover:cursor-pointer",
                props.minRows === TextAreaMinRows.MultiLine &&
                  "min-h-[8.25rem]",
                props.className
              )}
              className={clsxm(
                "box-border h-full px-4 pt-10 pb-2 text-black-900 focus:border-gray-150 focus:outline-none focus:ring-0",
                "prose-a:cursor-pointer prose-a:text-black-600",
                props.after && "pr-11"
              )}
              {...restProps}
              disabled={inactive}
              mode={props.mode}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              onTextSelectionUpdate={props.onTextSelectionUpdate}
              onMarkerSelected={props.onMarkerSelected}
            />
            <label
              htmlFor={id}
              style={{ width: labelMaxWidth }}
              className={clsxm(
                "mt absolute top-4 left-1t mr-8 origin-[0] -translate-y-2 px-4 text-black-500",
                "pointer-events-none transform overflow-hidden text-ellipsis whitespace-nowrap text-sm text-black-500 duration-300",
                focus || value.length > 0
                  ? "-translate-y-2 text-sm"
                  : "translate-y-0 scale-100 text-base",
                !props.disabled && "hover:cursor-pointer",
                props.after && "pr-11"
              )}
            >
              {props.label}
            </label>
            <div className="absolute top-0 right-0 mt-4 flex items-center gap-1 pr-4">
              {!!props.after && <>{props.after(focus)}</>}
              {(props.disabled || props.onDisabledToggle) && (
                <>
                  {props.onDisabledToggle ? (
                    <>
                      {props.disabled ? (
                        <Icon24Lock
                          className="text-black-400"
                          onClick={handleFreezeToggle}
                        />
                      ) : (
                        <Icon24Unlock
                          className="cursor-pointer text-black-400"
                          onClick={handleFreezeToggle}
                          active
                        />
                      )}
                    </>
                  ) : (
                    <Icon24Lock className="text-black-400" disabled={true} />
                  )}
                </>
              )}
              {props.enableVoiceRecorder && props.voiceRecorderComponent && (
                <>{props.voiceRecorderComponent}</>
              )}
            </div>
          </div>
        </div>
        {!error && !!props.description && (
          <div className="relative left-1t mt-1 ml-4 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-black-600">
            {props.description}
          </div>
        )}
        {error && (
          <div
            className="relative left-1t mt-1 overflow-hidden text-ellipsis whitespace-nowrap pl-4 text-sm text-blood-600"
            style={{ width: labelMaxWidth }}
          >
            {error}
          </div>
        )}
        {props.children ?? <></>}
      </div>
    );
  }
);

export default TextAreaWithRaisingLabel;
